import type { DeveloperUiSetting, UserDataLoginRecord } from '@navio/nsdk';
import { NoddiAsyncError } from 'noddi-types';
import { createContext } from 'react';

/* eslint-disable @typescript-eslint/no-explicit-any -- not typed yet: tokenLogin and tokenLoginOfUsers resolve to untyped login results */

/** A completed login: both token and user are guaranteed present (unlike the raw MFA-aware login response). */
export type UserSessionData = {
  token: string;
  user: UserDataLoginRecord;
};

interface AuthContextType {
  userData: UserSessionData | null;
  superUser: UserSessionData | null;
  updateUserData: (value: UserSessionData) => void;
  /** Patches the effective session (impersonated user when set, otherwise the real user). */
  setHideWhatsNew: (hideWhatsNew: boolean) => void;
  /** Patches the real session only. Impersonation must not write the customer row. */
  setDisabledDeveloperUiSettings: (disabledDeveloperUiSettings: DeveloperUiSetting[]) => void;
  impersonatedUser: UserSessionData | null;
  setImpersonatedUser: (value: UserSessionData | null) => void;
  updateImpersonatedData: (value: UserSessionData) => void;
  logout: () => Promise<void>;
  getCurrentUserGroupId: () => number | undefined;
  numberOfMembersInCurrentUserGroup: number | undefined;
  getCurrentUserGroupMemberId: () => number | undefined;
  currentUserGroupId?: number;
  isLoggedIn: boolean;
  tokenLogin: (token: string) => Promise<any>;
  getToken: () => string | null;
  getImpersonatedToken: () => string | null;
  /** Returns the active auth token, preferring an impersonated token when impersonation is active (for realtime, API calls as the effective user, etc.). */
  getActiveToken: () => string | null;
  canActivateCouponsForNewUsersOnly: boolean;
  isSuperUser: boolean;
  clearImpersonatedUser: () => void;
  isTokenLoginLoading: boolean;
  isTokenLoginError?: boolean;
  tokenLoginError: NoddiAsyncError | null;
  loginUser: (data: UserSessionData) => void;
  serviceDepartmentId?: number | null | undefined;
  hasCoupons: boolean | undefined;
  hasMemberships: boolean | undefined;
  hasTireHotel: boolean | undefined;
  tokenLoginOfUsers: () => Promise<any>;
  couldBeLoggedIn: boolean;
  isTokenLoadingOnMount: boolean;
  /** True if user should be allowed access. In local dev, allows access even if backend is down but user has a token. */
  shouldAllowAccess: boolean;
}

export const AuthContext = createContext<AuthContextType>({} as AuthContextType);
